<template>
  <div class="manager-dashboard">
    <!-- Header avec nom de l'école -->
    <dashboard-context-header title="MON ÉCOLE" @context-change="onContextChange" />

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Repas du jour -->
      <div class="card" @click="showMealDetails = true">
        <div class="card-header">
          <h3 class="card-title">🍽️ Repas du jour</h3>
          <span class="badge badge-blue">Aujourd'hui</span>
        </div>
        <p class="card-description">{{ mealCardDesc }}</p>
        <button class="card-link">Voir les détails →</button>
      </div>

      <!-- Card 2: Communications -->
      <div v-if="featureCommunications" class="card" @click="showCommunications = true">
        <div class="card-header">
          <h3 class="card-title">📢 Communications</h3>
          <span class="badge badge-orange" v-if="unreadMessages > 0">{{ unreadMessages }}</span>
        </div>
        <p class="card-description">Absences, spécificités alimentaires et messages</p>
        <button class="card-link">Voir les messages →</button>
      </div>

      <!-- Card 3: Effectifs -->
      <div v-if="featureEffectifs" class="card" @click="showEffectifs = true">
        <div class="card-header">
          <h3 class="card-title">👥 Effectifs</h3>
          <span class="badge badge-blue">Par niveau</span>
        </div>
        <p class="card-description">Configurer les effectifs prévus et spécificités alimentaires par niveau</p>
        <button class="card-link">Modifier →</button>
      </div>

      <!-- Card 3: Feedback -->
      <div class="card" @click="showFeedbackStats = true">
        <div class="card-header">
          <h3 class="card-title">📝 Feedback quotidien</h3>
          <span v-if="feedbackStats" class="badge badge-yellow">
            {{ feedbackStats.avgGlobal ? feedbackStats.avgGlobal.toFixed(1) + "/5" : "Nouveau" }}
          </span>
          <span v-else class="badge badge-yellow">En attente</span>
        </div>
        <p class="card-description">Retours superviseurs et votes enfants du jour</p>
        <button class="card-link">Voir →</button>
      </div>

      <!-- Card 4: Surplus -->
      <div v-if="featureSurplus" class="card" @click="showSurplusSection = true">
        <div class="card-header">
          <h3 class="card-title">📦 Gestion surplus</h3>
          <span class="badge badge-red" v-if="surplusCount > 0">
            {{ surplusCount }} offre{{ surplusCount > 1 ? "s" : "" }}
          </span>
        </div>
        <p class="card-description">Déclarer et valoriser les excédents alimentaires</p>
        <button class="card-link">Gérer →</button>
      </div>

      <!-- Card 5: Statistiques -->
      <div class="card" @click="showStats = true">
        <div class="card-header">
          <h3 class="card-title">📊 Statistiques</h3>
          <span class="badge badge-green">Cette semaine</span>
        </div>
        <p class="card-description">Satisfaction et gaspillage de la semaine</p>
        <button class="card-link">Consulter →</button>
      </div>

      <!-- Card 6: Gaspillage -->
      <div v-if="featureWaste" class="card" @click="showWasteSection = true">
        <div class="card-header">
          <h3 class="card-title">♻️ Gaspillage</h3>
          <span
            class="badge"
            :class="weeklyWasteTrend !== null && weeklyWasteTrend <= 0 ? 'badge-green' : 'badge-red'"
            v-if="weeklyWasteTrend !== null"
          >
            {{ weeklyWasteTrendLabel }}
          </span>
        </div>
        <p class="card-description">Visualisation et sensibilisation au gaspillage</p>
        <button class="card-link">Voir →</button>
      </div>

      <!-- Card 7: Import menus -->
      <div class="card" @click="showMenuImport = true">
        <div class="card-header">
          <h3 class="card-title">📥 Menus</h3>
          <span class="badge badge-blue">Import</span>
        </div>
        <p class="card-description">Importer les menus de la semaine depuis un fichier</p>
        <button class="card-link">Importer →</button>
      </div>

      <!-- Card 8: Services -->
      <div class="card" @click="showServiceManagement = true">
        <div class="card-header">
          <h3 class="card-title">⚙️ Services</h3>
          <span class="badge badge-blue">Paramétrage</span>
        </div>
        <p class="card-description">Configurer les services de restauration et leurs niveaux</p>
        <button class="card-link">Configurer →</button>
      </div>

      <!-- Card 9: Gestion du personnel -->
      <div v-if="canteenId" class="card" @click="goToTeamManagement">
        <div class="card-header">
          <h3 class="card-title">👩‍💼 Équipe</h3>
          <span class="badge badge-blue">Personnel</span>
        </div>
        <p class="card-description">Gérer les membres de l'équipe et leurs accès</p>
        <button class="card-link">Gérer l'équipe →</button>
      </div>

      <!-- Card 10: Export Excel (votes / déchets / effectifs) -->
      <a
        :href="exportUrl"
        class="card card-export"
        download
        aria-label="Télécharger l'export Excel des données de la cantine"
      >
        <div class="card-header">
          <h3 class="card-title">📊 Export Excel</h3>
          <span class="badge badge-green">Données</span>
        </div>
        <p class="card-description">Télécharger votes, gaspillage et effectifs (fichier .xlsx)</p>
        <span class="card-link">Télécharger →</span>
      </a>
    </div>

    <!-- Modal: Détails repas du jour -->
    <div v-if="showMealDetails" class="modal-overlay" @click="showMealDetails = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showMealDetails = false">✕</button>
        <h2 class="modal-title">🍽️ Repas du jour</h2>
        <p class="modal-subtitle">{{ mealSubtitle }}</p>

        <div class="detail-section">
          <h3 class="section-title">Effectifs</h3>
          <effectifs-panel
            :canteen-id="canteenId"
            :service-id="selectedService"
            :date="selectedDate"
            :readonly="true"
            :variant="effectifVariant"
          />
        </div>

        <div class="detail-section">
          <h3 class="section-title">Menu</h3>
          <menu-du-jour-panel :canteen-id="canteenId" :date="selectedDate" />
        </div>
      </div>
    </div>

    <!-- Modal: Communications -->
    <div v-if="showCommunications && featureCommunications" class="modal-overlay" @click="showCommunications = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showCommunications = false">✕</button>
        <communication-panel2
          :canteen-id="canteenId"
          :date="selectedDate"
          :variant="effectifVariant"
          :service-id="selectedService"
        />
      </div>
    </div>

    <!-- Modal: Effectifs -->
    <div v-if="showEffectifs && featureEffectifs" class="modal-overlay" @click="showEffectifs = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showEffectifs = false">✕</button>
        <h2 class="modal-title">👥 Effectifs</h2>
        <effectifs-panel
          :canteen-id="canteenId"
          :service-id="selectedService"
          :date="selectedDate"
          :readonly="false"
          :variant="effectifVariant"
        />
      </div>
    </div>

    <!-- Modal: Feedback Stats -->
    <div v-if="showFeedbackStats" class="modal-overlay" @click="showFeedbackStats = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showFeedbackStats = false">✕</button>
        <h2 class="modal-title">📝 Feedback du jour</h2>
        <p class="modal-subtitle">{{ selectedDate }}</p>

        <div v-if="feedbackStatsLoading" class="stats-row">
          <p style="text-align:center;color:#6b7280">Chargement…</p>
        </div>
        <div v-else-if="feedbackStats">
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">{{ feedbackStats.avgGlobal ? feedbackStats.avgGlobal.toFixed(1) : "—" }}</div>
              <div class="stat-label">Note moyenne /5</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ feedbackStats.total || 0 }}</div>
              <div class="stat-label">Feedbacks reçus</div>
            </div>
          </div>
          <div
            v-if="feedbackStats.comments && feedbackStats.comments.length > 0"
            class="detail-section"
            style="margin-top:1rem"
          >
            <h3 class="section-title">Commentaires</h3>
            <div v-for="(comment, i) in feedbackStats.comments" :key="i" class="reservation-card">
              <p style="margin:0;font-size:0.875rem;font-weight:600;color:#374151">{{ comment.supervisor }}</p>
              <p style="margin:0.25rem 0 0;font-size:0.875rem;color:#374151">{{ comment.comment }}</p>
            </div>
          </div>
        </div>
        <p v-else style="text-align:center;color:#6b7280;padding:1rem">Aucun feedback pour aujourd'hui.</p>
      </div>
    </div>

    <!-- Modal: Feedback Form -->
    <div v-if="showFeedbackForm" class="modal-overlay" @click="showFeedbackForm = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showFeedbackForm = false">✕</button>
        <feedback-form
          :canteen-id="canteenId"
          :service-id="selectedService"
          :meal-date="selectedDate"
          @close="showFeedbackForm = false"
        />
      </div>
    </div>

    <!-- Modal: Surplus -->
    <div v-if="showSurplusSection" class="modal-overlay" @click="showSurplusSection = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showSurplusSection = false">✕</button>
        <h2 class="modal-title">📦 Gestion des surplus</h2>

        <div class="detail-section">
          <div v-if="surplusWeightKg > 0" class="alert alert-warning">
            <strong>⚠️ Alerte</strong>
            <p>{{ formattedSurplusWeight }}kg de surplus à valoriser aujourd'hui</p>
          </div>
          <div v-else class="alert alert-info">
            <strong>ℹ️ Information</strong>
            <p>Aucun surplus à valoriser aujourd'hui</p>
          </div>
        </div>

        <button
          @click="
            showSurplusForm = true
            showSurplusSection = false
          "
          class="btn btn-primary"
        >
          Déclarer un surplus
        </button>
        <button class="btn btn-secondary">Voir les associations disponibles</button>
      </div>
    </div>

    <!-- Modal: Surplus Form -->
    <div v-if="showSurplusForm" class="modal-overlay" @click="showSurplusForm = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showSurplusForm = false">✕</button>
        <surplus-form
          :canteen-id="canteenId"
          :service-id="selectedService"
          :meal-date="selectedDate"
          @close="
            showSurplusForm = false
            loadSurplusCount()
          "
        />
      </div>
    </div>

    <!-- Modal: Statistiques -->
    <div v-if="showStats" class="modal-overlay" @click="showStats = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showStats = false">✕</button>
        <h2 class="modal-title">📊 Statistiques de la semaine</h2>

        <div class="stats-row">
          <div class="stat-card stat-green">
            <div class="stat-icon">😊</div>
            <div class="stat-value">{{ weeklySatisfaction !== null ? weeklySatisfaction + "%" : "n/d" }}</div>
            <div class="stat-label">Satisfaction</div>
          </div>
          <div class="stat-card stat-blue">
            <div class="stat-icon">{{ weeklyWasteTrend !== null && weeklyWasteTrend <= 0 ? "📉" : "📈" }}</div>
            <div class="stat-value">{{ weeklyWasteTrendLabel }}</div>
            <div class="stat-label">Gaspillage</div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Plats à surveiller</h3>
          <div v-if="weeklyWatchDishes.length > 0" class="ranking-list">
            <div v-for="(dish, idx) in weeklyWatchDishes" :key="dish.name" class="ranking-item">
              <span class="ranking-number">{{ idx + 1 }}</span>
              <span class="ranking-name">{{ dish.name }}</span>
              <span class="ranking-score">{{ dish.count }} signalement{{ dish.count > 1 ? "s" : "" }}</span>
            </div>
          </div>
          <p v-else class="empty-hint">
            {{ statsLoading ? "Chargement des statistiques…" : "Aucun plat signalé cette semaine." }}
          </p>
        </div>
      </div>
    </div>

    <!-- Modal: Gaspillage -->
    <div v-if="showWasteSection && featureWaste" class="modal-overlay" @click="showWasteSection = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showWasteSection = false">✕</button>
        <waste-input-panel
          :canteen-id="canteenId"
          :variant="wasteVariant"
          :meal-date="selectedDate"
          :service-id="selectedService"
        />
      </div>
    </div>

    <!-- Modal: Import menus -->
    <div v-if="showMenuImport" class="modal-overlay" @click="showMenuImport = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showMenuImport = false">✕</button>
        <h2 class="modal-title">📥 Importer les menus</h2>
        <p class="modal-subtitle">Fichier Markdown généré depuis le PDF de la cuisine centrale</p>
        <menu-import-panel :canteen-id="canteenId" @imported="onMenusImported" />
      </div>
    </div>

    <!-- Modal: Gestion des services -->
    <div v-if="showServiceManagement" class="modal-overlay" @click="showServiceManagement = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showServiceManagement = false">✕</button>
        <h2 class="modal-title">⚙️ Services de restauration</h2>
        <p class="modal-subtitle">Gérer les services et leurs niveaux (effectifs par type de repas)</p>
        <service-management-panel :canteen-id="canteenId" />
      </div>
    </div>
  </div>
</template>

<script>
import FeedbackForm from "./FeedbackForm.vue"
import SurplusForm from "./SurplusForm.vue"
import CommunicationPanel2 from "@/components/CommunicationPanel2.vue"
import WasteInputPanel from "@/components/WasteInputPanel.vue"
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"
import EffectifsPanel from "@/components/EffectifsPanel.vue"
import MenuDuJourPanel from "@/components/MenuDuJourPanel.vue"
import MenuImportPanel from "@/components/MenuImportPanel.vue"
import ServiceManagementPanel from "@/components/ServiceManagementPanel.vue"

export default {
  name: "ManagerDashboard3",
  components: {
    FeedbackForm,
    SurplusForm,
    CommunicationPanel2,
    WasteInputPanel,
    DashboardContextHeader,
    EffectifsPanel,
    MenuDuJourPanel,
    MenuImportPanel,
    ServiceManagementPanel,
  },
  data() {
    return {
      showMealDetails: false,
      showCommunications: false,
      showFeedbackForm: false,
      showFeedbackStats: false,
      showSurplusSection: false,
      showSurplusForm: false,
      showStats: false,
      showWasteSection: false,
      showEffectifs: false,
      showMenuImport: false,
      showServiceManagement: false,
      unreadMessages: 0,
      hasSurplus: false,
      surplusCount: 0,
      surplusWeightKg: 0,
      feedbackStats: null,
      feedbackStatsLoading: false,
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
      effectifVariant: "SIMPLE",
      wasteVariant: "barquette",
      featureEffectifs: true,
      featureCommunications: true,
      featureWaste: true,
      featureSurplus: true,
      featureSupervisorFeedback: true,
      totalPrevus: null,
      statsLoading: false,
      weeklySatisfaction: null,
      weeklyWasteTrend: null,
      weeklyWatchDishes: [],
    }
  },
  computed: {
    mealSubtitle() {
      // Date du jour sélectionnée, formatée en français. On reconstruit la date
      // à partir des composants (et non `new Date("YYYY-MM-DD")` qui parse en UTC
      // et peut décaler d'un jour selon le fuseau).
      let dateLabel = ""
      if (this.selectedDate) {
        const [y, m, d] = this.selectedDate.split("-").map(Number)
        const dt = new Date(y, m - 1, d)
        dateLabel = dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
        dateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)
      }
      return dateLabel ? `${dateLabel} - Service du midi` : "Service du midi"
    },
    mealCardDesc() {
      if (!this.selectedService) return "Sélectionnez un service"
      if (this.totalPrevus === null) return "Chargement des effectifs…"
      return `${this.totalPrevus} élève${this.totalPrevus !== 1 ? "s" : ""} attendu${this.totalPrevus !== 1 ? "s" : ""}`
    },
    formattedSurplusWeight() {
      // Affichage compact : pas de décimale superflue (8 plutôt que 8.0).
      const kg = Math.round(this.surplusWeightKg * 10) / 10
      return Number.isInteger(kg) ? String(kg) : kg.toFixed(1)
    },
    weeklyWasteTrendLabel() {
      // Tendance gaspillage : signe explicite, « n/d » si non calculable.
      if (this.weeklyWasteTrend === null) return "n/d"
      return `${this.weeklyWasteTrend > 0 ? "+" : ""}${this.weeklyWasteTrend}%`
    },
    exportUrl() {
      // Export Excel agrégé (votes / déchets / effectifs), scoppé à la cantine
      // courante. Téléchargement direct via <a download> (auth par cookie de session).
      const base = "/api/v1/export-labricot.xlsx"
      return this.canteenId ? `${base}?canteen=${this.canteenId}` : base
    },
  },
  methods: {
    onContextChange({
      canteenId,
      selectedDate,
      selectedService,
      effectifVariant,
      wasteVariant,
      featureEffectifs,
      featureCommunications,
      featureWaste,
      featureSurplus,
      featureSupervisorFeedback,
    }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
      this.effectifVariant = effectifVariant || "SIMPLE"
      this.wasteVariant = wasteVariant || "barquette"
      this.featureEffectifs = featureEffectifs ?? true
      this.featureCommunications = featureCommunications ?? true
      this.featureWaste = featureWaste ?? true
      this.featureSurplus = featureSurplus ?? true
      this.featureSupervisorFeedback = featureSupervisorFeedback ?? true
      if (canteenId && selectedDate) {
        this.loadFeedbackStats()
        this.loadSurplusCount()
        this.loadWeeklyStats()
      }
      if (selectedService) {
        this.loadEffectifSummary()
      }
    },

    async loadFeedbackStats() {
      if (!this.canteenId || !this.selectedDate) return
      this.feedbackStats = null
      this.feedbackStatsLoading = true
      try {
        const resp = await fetch(
          `/api/v1/canteen/${this.canteenId}/supervisor-feedback-stats/?date=${this.selectedDate}`,
          { credentials: "same-origin" }
        )
        if (resp.ok) {
          this.feedbackStats = await resp.json()
        }
      } catch {
        // silencieux
      } finally {
        this.feedbackStatsLoading = false
      }
    },

    async loadSurplusCount() {
      if (!this.canteenId || !this.selectedDate) return
      try {
        const resp = await fetch(
          `/api/v1/canteen/${this.canteenId}/surplus-offers/?date=${this.selectedDate}&is_available=true`,
          { credentials: "same-origin" }
        )
        if (resp.ok) {
          const data = await resp.json()
          const offers = Array.isArray(data) ? data : data.results || []
          this.surplusCount = Array.isArray(data) ? data.length : data.count || offers.length
          this.hasSurplus = this.surplusCount > 0
          // Poids total à valoriser = somme des quantités (kg) déclarées sur la
          // journée. `items` est un objet unique {dish_name, quantity_kg, ...}
          // (clés en snake_case, non camélisées car contenu d'un JSONField) ;
          // on tolère aussi un tableau d'items par prudence.
          const itemKg = (item) => Number(item && (item.quantity_kg ?? item.quantityKg)) || 0
          this.surplusWeightKg = offers.reduce((total, offer) => {
            const items = Array.isArray(offer.items) ? offer.items : offer.items ? [offer.items] : []
            return total + items.reduce((sum, item) => sum + itemKg(item), 0)
          }, 0)
        }
      } catch {
        // silencieux
      }
    },

    weekDates(refDateStr, weekOffset = 0) {
      // Renvoie les dates ISO (lundi → vendredi) de la semaine contenant refDate,
      // décalée de `weekOffset` semaines. Reconstruction par composants pour
      // éviter le décalage UTC de `new Date("YYYY-MM-DD")`.
      if (!refDateStr) return []
      const [y, m, d] = refDateStr.split("-").map(Number)
      const ref = new Date(y, m - 1, d)
      const dow = (ref.getDay() + 6) % 7 // 0 = lundi
      const monday = new Date(ref)
      monday.setDate(ref.getDate() - dow + weekOffset * 7)
      const days = []
      for (let i = 0; i < 5; i++) {
        const dt = new Date(monday)
        dt.setDate(monday.getDate() + i)
        const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(
          2,
          "0"
        )}`
        days.push(iso)
      }
      return days
    },

    async loadWeeklyStats() {
      if (!this.canteenId || !this.selectedDate) return
      this.statsLoading = true
      try {
        const currentWeek = this.weekDates(this.selectedDate, 0)
        const previousWeek = this.weekDates(this.selectedDate, -1)

        // --- Votes enfants de la semaine : satisfaction + plats à surveiller ---
        const voteResults = await Promise.all(
          currentWeek.map((date) =>
            fetch(`/api/v1/canteen/${this.canteenId}/child-votes-stats/?date=${date}`, {
              credentials: "same-origin",
            })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        )
        let liked = 0
        let totalVotes = 0
        const watchCounts = {}
        voteResults.filter(Boolean).forEach((stats) => {
          liked += stats.liked || 0
          totalVotes += stats.totalVotes || 0
          const problematic = stats.problematicDishesStats || {}
          Object.keys(problematic).forEach((dish) => {
            watchCounts[dish] = (watchCounts[dish] || 0) + problematic[dish]
          })
        })
        this.weeklySatisfaction = totalVotes > 0 ? Math.round((liked / totalVotes) * 100) : null
        this.weeklyWatchDishes = Object.keys(watchCounts)
          .map((name) => ({ name, count: watchCounts[name] }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3)

        // --- Gaspillage : tendance semaine courante vs semaine précédente ---
        const sumWeekWaste = async (week) => {
          const results = await Promise.all(
            week.map((date) =>
              fetch(`/api/v1/canteen/${this.canteenId}/waste-measurements/?date=${date}`, {
                credentials: "same-origin",
              })
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null)
            )
          )
          return results.filter(Boolean).reduce((total, payload) => {
            const list = Array.isArray(payload) ? payload : payload.results || []
            return total + list.reduce((sum, m) => sum + (Number(m.totalWeightKg) || 0), 0)
          }, 0)
        }
        const [currentWaste, previousWaste] = await Promise.all([sumWeekWaste(currentWeek), sumWeekWaste(previousWeek)])
        // Tendance non calculable sans référence (semaine précédente vide) → null.
        this.weeklyWasteTrend =
          previousWaste > 0 ? Math.round(((currentWaste - previousWaste) / previousWaste) * 100) : null
      } catch {
        // silencieux : le modal affiche alors les états « données insuffisantes »
      } finally {
        this.statsLoading = false
      }
    },

    onMenusImported(result) {
      this.showMenuImport = false
      this.$store.dispatch("notify", {
        message: `✅ ${result.total} plats importés (${result.created} nouveaux, ${result.updated} mis à jour)`,
        status: "success",
      })
    },

    goToTeamManagement() {
      this.$router.push(`/modifier-ma-cantine/${this.canteenId}/equipe/`)
    },

    async loadEffectifSummary() {
      if (!this.selectedService) {
        this.totalPrevus = null
        return
      }
      try {
        const levels = await this.$store.dispatch("fetchServiceLevels", this.selectedService)
        this.totalPrevus = Array.isArray(levels)
          ? levels.reduce((s, l) => s + (l.nbStandardMeals ?? l.nb_standard_meals ?? 0), 0)
          : 0
      } catch {
        this.totalPrevus = null
      }
    },
  },
}
</script>

<style scoped>
/* Layout global */
.manager-dashboard {
  max-width: 1201px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Header */
.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.5px;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

/* Grille de cartes */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

/* Cartes */
.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

/* La carte d'export est un lien <a download> : neutraliser le style de lien
   pour qu'elle se comporte visuellement comme les autres cartes. */
.card-export {
  display: block;
  text-decoration: none;
  color: inherit;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.card-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s;
}

.card-link:hover {
  color: #1d4ed8;
}

/* Badges */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}

.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}

.badge-yellow {
  background: #fef3c7;
  color: #92400e;
}

.badge-red {
  background: #fecaca;
  color: #991b1b;
}

.badge-green {
  background: #d1fae5;
  color: #065f46;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e5e7eb;
  color: #1a1a1a;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Sections de détails */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

/* Stats row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
}

.stat-item {
  text-align: center;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid;
}

.stat-card.stat-green {
  border-color: #86efac;
  background: #f0fdf4;
}

.stat-card.stat-blue {
  border-color: #93c5fd;
  background: #eff6ff;
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

/* Menu list */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

/* Ranking list */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.ranking-number {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
}

.ranking-name {
  flex: 1;
  font-weight: 500;
  color: #1a1a1a;
}

.ranking-score {
  font-weight: 700;
  color: #16a34a;
}

.empty-hint {
  color: #6b7280;
  font-style: italic;
  padding: 0.5rem 0;
}

/* Alerts */
.alert {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.alert-warning {
  background: #fef3c7;
  border-color: #f59e0b;
}

.alert-info {
  background: #eff6ff;
  border-color: #3b82f6;
}

.alert strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #92400e;
}

.alert p {
  margin: 0;
  font-size: 0.875rem;
  color: #78350f;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.75rem;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #f3f4f6;
  color: #1a1a1a;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* Responsive */
@media (max-width: 640px) {
  .manager-dashboard {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.5rem;
  }

  .modal-content {
    padding: 1.5rem;
    border-radius: 12px;
  }
}
</style>
